import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, orderBy, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Plus, Edit, Trash2, Eye, EyeOff, Star } from "lucide-react";
import { formatPrice, formatDate } from "../utils/format";
import toast from "react-hot-toast";

export default function AdminProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  async function load() {
    const snap = await getDocs(query(collection(db, "products"), orderBy("createdAt", "desc")));
    setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function toggleStatus(id, current) {
    await updateDoc(doc(db, "products", id), { status: current === "active" ? "inactive" : "active" });
    toast.success("Status updated");
    load();
  }

  async function toggleFeatured(id, current) {
    await updateDoc(doc(db, "products", id), { featured: !current });
    toast.success(current ? "Removed from featured" : "Marked as featured");
    load();
  }

  async function deleteProduct(id, name) {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    await deleteDoc(doc(db, "products", id));
    toast.success("Product deleted");
    load();
  }

  const filtered = products.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ padding: "40px 48px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>Products</h1>
          <p style={{ color: "var(--text-muted)", fontSize: 15, marginTop: 4 }}>Manage your catalog, stock, and visibility.</p>
        </div>
        <Link to="/admin/products/add" className="btn btn-primary" style={{ padding: "12px 20px", fontSize: 14 }}><Plus size={18} /> Add Product</Link>
      </div>

      <div style={{ marginBottom: 24 }}>
        <input className="form-input" placeholder="Search products by name or category…" value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 400, padding: "12px 16px", borderRadius: 10 }} />
      </div>

      <div className="card" style={{ overflow: "hidden", background: "#fff", border: "1px solid var(--border)" }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: "center" }}><div className="loader"><div className="spinner" /></div></div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}>
                  {["Product", "Category", "Price", "Stock", "Status", "Featured", "Actions"].map(h => (
                    <th key={h} style={{ padding: "16px 24px", textAlign: "left", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} style={{ borderBottom: "1px solid var(--border)", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "16px 24px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 8, overflow: "hidden", background: "var(--surface)", border: "1px solid var(--border)", flexShrink: 0 }}>
                        {p.images?.[0] ? <img src={p.images[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>📦</div>}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 14, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Added {formatDate(p.createdAt)}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "16px 24px", color: "var(--text-muted)", fontSize: 13, fontWeight: 500 }}>{p.category}</td>
                  <td style={{ padding: "16px 24px", fontWeight: 700, color: "var(--text)" }}>{formatPrice(p.price)}</td>
                  <td style={{ padding: "16px 24px" }}>
                    <span style={{ 
                      padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700,
                      background: p.stock > 5 ? "rgba(16, 185, 129, 0.1)" : p.stock > 0 ? "rgba(245, 166, 35, 0.1)" : "rgba(239, 68, 68, 0.1)",
                      color: p.stock > 5 ? "#10B981" : p.stock > 0 ? "#F5A623" : "#EF4444" 
                    }}>
                      {p.stock ?? "—"}
                    </span>
                  </td>
                  <td style={{ padding: "16px 24px" }}>
                    <button onClick={() => toggleStatus(p.id, p.status)}
                      style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: p.status === "active" ? "#10B981" : "var(--text-muted)", transition: "opacity 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.opacity = 0.7}
                      onMouseLeave={e => e.currentTarget.style.opacity = 1}>
                      {p.status === "active" ? <Eye size={16} /> : <EyeOff size={16} />}
                      {p.status === "active" ? "Active" : "Hidden"}
                    </button>
                  </td>
                  <td style={{ padding: "16px 24px" }}>
                    <button onClick={() => toggleFeatured(p.id, p.featured)}
                      style={{ border: "none", background: "transparent", cursor: "pointer", color: p.featured ? "#F5A623" : "var(--text-muted)", transition: "transform 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.15)"}
                      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                      <Star size={18} fill={p.featured ? "#F5A623" : "none"} strokeWidth={p.featured ? 0 : 2} />
                    </button>
                  </td>
                  <td style={{ padding: "16px 24px" }}>
                    <div style={{ display: "flex", gap: 12 }}>
                      <Link to={`/admin/products/edit/${p.id}`} style={{ color: "#3B82F6", padding: 6, borderRadius: 6, background: "rgba(59, 130, 246, 0.1)", display: "flex", alignItems: "center", transition: "background 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(59, 130, 246, 0.2)"}
                        onMouseLeave={e => e.currentTarget.style.background = "rgba(59, 130, 246, 0.1)"}>
                        <Edit size={16} />
                      </Link>
                      <button onClick={() => deleteProduct(p.id, p.name)} style={{ border: "none", color: "#EF4444", padding: 6, borderRadius: 6, background: "rgba(239, 68, 68, 0.1)", display: "flex", alignItems: "center", cursor: "pointer", transition: "background 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(239, 68, 68, 0.2)"}
                        onMouseLeave={e => e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)"}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: 48, color: "var(--text-muted)" }}>
            No products found. <Link to="/admin/products/add" style={{ color: "var(--brand)" }}>Add one →</Link>
          </div>
        )}
      </div>
    </div>
  );
}
